/**
 * CRM permissions per role - the CRM rights of the host site's roles, edited
 * inside the CRM app.
 *
 * Reuses the SHARED RBAC engine (`/api/admin/rbac/*`); there is no new backend.
 * The difference from `web_admin/roles` is the filter: only `category:"crm"`
 * permissions are shown (the Business Register plus the CRM modules). The
 * endpoints are `requirePermission("manage:roles") + requireSite`, so we append
 * `?siteId=<host>` (the web_crm API client does not send `X-Site-Id`) - CRM
 * rights are platform-scoped and grantable only on the host site.
 *
 * CRITICAL: the PATCH replaces ALL of a role's grants (deleteMany+createMany).
 * That is why we save `the role's non-CRM grants union the edited CRM grants` -
 * otherwise we would wipe the site rights (manage:pages and friends) that this
 * screen never displays.
 *
 * This file is the screen: what it loads, what it saves, and how the two cards
 * sit. The row and the rights form live in `./roles`.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, ShieldCheck, Users } from "lucide-react";
import { useI18n, useT } from "../../../i18n/I18nProvider";
import { api } from "../../../lib/api";
import { hostSiteId, type StaffUser } from "../../../lib/auth";
import { Card, CardHead, EmptyState, listCard } from "../../ui";
import { PopHost } from "../../shell/Pop";
import {
  CrmPermissionsCard, ROLE_COLS, RoleListRow, type Permission, type RoleDetail, type RoleRow,
} from "./roles";
import { PeopleCard } from "./people";

export function RolesSettings({ user }: { user: StaffUser }) {
  const t = useT();
  const { locale } = useI18n();
  const siteId = hostSiteId(user);
  const q = siteId ? `?siteId=${encodeURIComponent(siteId)}` : "";

  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [crmPerms, setCrmPerms] = useState<Permission[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [detail, setDetail] = useState<RoleDetail | null>(null);
  const [grants, setGrants] = useState<Set<string>>(new Set()); // CRM keys only
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const crmKeySet = useMemo(() => new Set(crmPerms.map((p) => p.key)), [crmPerms]);

  const loadAll = useCallback(async () => {
    if (!siteId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [r, p] = await Promise.all([
        api.get<RoleRow[]>(`/admin/rbac/roles${q}`),
        api.get<Permission[]>(`/admin/rbac/permissions${q}`),
      ]);
      setRoles(r);
      setCrmPerms(p.filter((x) => x.category === "crm"));
      setActiveId((cur) => (cur && r.some((x) => x.id === cur) ? cur : r[0]?.id ?? null));
    } finally {
      setLoading(false);
    }
  }, [siteId, q]);

  useEffect(() => { void loadAll(); }, [loadAll]);

  useEffect(() => {
    if (!activeId) { setDetail(null); setGrants(new Set()); return; }
    void api.get<RoleDetail>(`/admin/rbac/roles/${activeId}${q}`).then((d) => {
      setDetail(d);
      setGrants(new Set(d.permissionKeys.filter((k) => crmKeySet.has(k))));
    });
  }, [activeId, q, crmKeySet]);

  const isSuper = detail?.isSuperadmin ?? false;
  const inheritedCrm = useMemo(
    () => new Set((detail?.inheritedPermissionKeys ?? []).filter((k) => crmKeySet.has(k))),
    [detail, crmKeySet],
  );
  // The role's grants that this screen does NOT display (site rights and the
  // like) - they have to survive the save.
  const ownNonCrm = useMemo(
    () => (detail ? detail.permissionKeys.filter((k) => !crmKeySet.has(k)) : []),
    [detail, crmKeySet],
  );

  const dirty = useMemo(() => {
    if (!detail) return false;
    const orig = new Set(detail.permissionKeys.filter((k) => crmKeySet.has(k)));
    if (orig.size !== grants.size) return true;
    for (const k of grants) if (!orig.has(k)) return true;
    return false;
  }, [detail, grants, crmKeySet]);

  function togglePerm(key: string) {
    if (isSuper || inheritedCrm.has(key)) return;
    setGrants((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function toggleAll(allOn: boolean) {
    if (isSuper) return;
    setGrants((prev) => {
      const next = new Set(prev);
      for (const p of crmPerms) {
        if (inheritedCrm.has(p.key)) continue;
        (allOn ? next.delete(p.key) : next.add(p.key));
      }
      return next;
    });
  }

  async function save() {
    if (!detail || isSuper) return;
    setSaving(true);
    try {
      // Keep the non-CRM grants; overwrite only the CRM part.
      await api.patch(`/admin/rbac/roles/${detail.id}${q}`, { permissionKeys: [...ownNonCrm, ...grants] });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
      await loadAll();
      const fresh = await api.get<RoleDetail>(`/admin/rbac/roles/${detail.id}${q}`);
      setDetail(fresh);
      setGrants(new Set(fresh.permissionKeys.filter((k) => crmKeySet.has(k))));
    } finally {
      setSaving(false);
    }
  }

  if (!siteId) {
    return (
      <Card>
        <EmptyState icon={ShieldCheck} title={t("roles.noHost")} />
      </Card>
    );
  }

  const total = crmPerms.length;
  const grantedCount = isSuper ? total : new Set([...grants, ...inheritedCrm]).size;

  /** How much of the CRM a row's role opens. The list endpoint counts a role's
   *  grants across every category, so only the open role - whose detail we have
   *  - can honestly be shown as a share of the CRM's own rights. */
  const rightsFor = (r: RoleRow) =>
    r.isSuperadmin ? `${total}/${total}` : r.id === activeId ? `${grantedCount}/${total}` : `${r.permissionCount ?? 0}`;

  return (
    // Settings mounts no `PopHost` of its own, and a `Pop` without one is a
    // button that opens nothing and says nothing about it in the console.
    <PopHost>
      <div className="space-y-4">
        <Card style={listCard(ROLE_COLS)}>
          <CardHead
            title={
              <span className="flex items-center gap-2.5">
                <ShieldCheck className="size-[17px] shrink-0" style={{ color: "var(--color-primary)" }} />
                {t("roles.title")}
              </span>
            }
            sub={t("roles.subtitle")}
          />

          {loading ? (
            <div className="flex justify-center py-16 text-muted-foreground">
              <Loader2 className="size-6 animate-spin" />
            </div>
          ) : roles.length === 0 ? (
            <EmptyState icon={Users} title={t("roles.emptyTitle")} body={t("roles.emptyBody")} />
          ) : (
            <>
              <div className="crm-list-head" style={{ gridTemplateColumns: ROLE_COLS, paddingTop: 18 }}>
                <span>{t("roles.col.role")}</span>
                <span>{t("roles.col.kind")}</span>
                <span>{t("roles.col.members")}</span>
                <span>{t("roles.col.rights")}</span>
                <span />
              </div>

              <div className="crm-rows pt-0">
                {roles.map((r) => (
                  <RoleListRow
                    key={r.id}
                    role={r}
                    locale={locale}
                    active={r.id === activeId}
                    rights={rightsFor(r)}
                    onSelect={() => setActiveId(r.id)}
                  />
                ))}
              </div>
            </>
          )}
        </Card>

        {detail ? (
          <CrmPermissionsCard
            detail={detail}
            locale={locale}
            perms={crmPerms}
            grants={grants}
            inherited={inheritedCrm}
            grantedCount={grantedCount}
            saving={saving}
            dirty={dirty}
            saved={savedAt !== null}
            onToggle={togglePerm}
            onToggleAll={toggleAll}
            onSave={() => void save()}
          />
        ) : null}
      </div>

      {/* Below the two role cards, and deliberately full width: the cards above
          answer "what may a role do", this answers "who holds one". Reading it
          in the other order asks the operator to pick a role for a person they
          have not added yet. */}
      <div className="pt-4">
        <PeopleCard />
      </div>
    </PopHost>
  );
}
